import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  IconButton,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  LinearProgress,
  Tooltip,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Refresh,
  TrendingUp,
  TrendingDown,
  Remove,
  Dashboard as DashboardIcon,
} from '@mui/icons-material';
// import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from 'recharts';
import APIKit from '../../../config/APIKit';
import SimpleCard from '../../components/SimpleCard';
import swal from 'sweetalert';
import { showSnackBar } from '../../store/compatibility';
import { useDispatch } from 'react-redux';
import { COLORS } from '../../core/constants';

export default function DashboardView({ dashboardId }) {
  const dispatch = useDispatch();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [widgetModal, setWidgetModal] = useState({ open: false, widget: null });
  const [kpis, setKpis] = useState([]);

  useEffect(() => {
    if (dashboardId) {
      loadDashboard();
      loadKPIs();
    }
  }, [dashboardId]);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const response = await APIKit.get(`/dashboards/${dashboardId}`);
      if (response.success) {
        setDashboard(response.data);
      }
    } catch (error) {
      console.error('Error loading dashboard:', error);
      dispatch(showSnackBar({ message: 'Error loading dashboard', severity: 'error' }));
    } finally {
      setLoading(false);
    }
  };

  const loadKPIs = async () => {
    try {
      const response = await APIKit.get('/kpis?is_active=true');
      if (response.success) {
        setKpis(response.data || []);
      }
    } catch (error) {
      console.error('Error loading KPIs:', error);
    }
  };

  const handleAddWidget = () => {
    setWidgetModal({ open: true, widget: null });
  };

  const handleDeleteWidget = async (widgetId) => {
    const result = await swal({
      title: 'Are you sure?',
      text: 'This will remove the widget from the dashboard',
      icon: 'warning',
      buttons: true,
      dangerMode: true,
    });

    if (result) {
      try {
        await APIKit.delete(`/dashboard-widgets/${widgetId}`);
        dispatch(showSnackBar({ message: 'Widget removed successfully', severity: 'success' }));
        loadDashboard();
      } catch (error) {
        dispatch(showSnackBar({ message: 'Error removing widget', severity: 'error' }));
      }
    }
  };

  const renderWidget = (widget) => {
    const kpi = widget.KPIDefinition;
    if (!kpi) return null;

    const values = kpi.Values || [];
    const latestValue = values[0];
    const previousValue = values[1];

    // Calculate trend
    let trend = 'stable';
    let trendIcon = <Remove />;
    let trendColor = 'default';
    if (latestValue && previousValue) {
      const change = ((parseFloat(latestValue.value) - parseFloat(previousValue.value)) / parseFloat(previousValue.value)) * 100;
      if (change > 5) {
        trend = 'increasing';
        trendIcon = <TrendingUp />;
        trendColor = 'success';
      } else if (change < -5) {
        trend = 'decreasing';
        trendIcon = <TrendingDown />;
        trendColor = 'error';
      }
    }

    // Prepare chart data
    const chartData = values.slice(0, 10).reverse().map(v => ({
      date: new Date(v.period_start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      value: parseFloat(v.value),
      target: parseFloat(v.target_value) || 0,
    }));

    return (
      <Grid size={{xs:12, md:widget.width || 6}} key={widget.id}>
        <Card sx={{ height: '100%', position: 'relative' }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
              <Box>
                <Typography variant="h6">{widget.title || kpi.name}</Typography>
                <Typography variant="caption" color="textSecondary">
                  {kpi.description}
                </Typography>
              </Box>
              <Box>
                <IconButton size="small" onClick={() => handleDeleteWidget(widget.id)}>
                  <Delete fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            {/* Value Display */}
            {kpi.visualization_type === 'number' && (
              <Box>
                <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {latestValue ? parseFloat(latestValue.value).toLocaleString() : '0'}
                  {kpi.unit && <Typography component="span" variant="h6" sx={{ ml: 1 }}>{kpi.unit}</Typography>}
                </Typography>
                {kpi.target_value && (
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      Target: {parseFloat(kpi.target_value).toLocaleString()} {kpi.unit}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={latestValue ? (parseFloat(latestValue.value) / parseFloat(kpi.target_value)) * 100 : 0}
                      sx={{ mt: 1, height: 8, borderRadius: 4 }}
                    />
                  </Box>
                )}
                {latestValue && previousValue && (
                  <Chip
                    icon={trendIcon}
                    label={`${trend}`}
                    color={trendColor}
                    size="small"
                    sx={{ mt: 1 }}
                  />
                )}
              </Box>
            )}

            {/* Line Chart */}
            {kpi.visualization_type === 'line_chart' && chartData.length > 0 && (
              <Box sx={{ mt: 2, p: 2, border: '1px dashed #ccc', borderRadius: 2 }}>
                <Typography variant="h4" sx={{ mb: 2 }}>
                  {latestValue ? parseFloat(latestValue.value).toLocaleString() : '0'} {kpi.unit}
                </Typography>
                <Typography variant="body2" color="textSecondary" align="center" sx={{ py: 4 }}>
                  📊 Chart visualization requires recharts package<br/>
                  Run: <code>npm install recharts</code>
                </Typography>
                {/* Placeholder for chart data */}
                <Box sx={{ mt: 2 }}>
                  {chartData.slice(0, 5).map((point, index) => (
                    <Box key={index} display="flex" justifyContent="space-between" sx={{ mb: 1 }}>
                      <Typography variant="body2">{point.date}</Typography>
                      <Typography variant="body2">{point.value}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Bar Chart */}
            {kpi.visualization_type === 'bar_chart' && chartData.length > 0 && (
              <Box sx={{ mt: 2, p: 2, border: '1px dashed #ccc', borderRadius: 2 }}>
                <Typography variant="body2" color="textSecondary" align="center" sx={{ py: 4 }}>
                  📊 Bar chart visualization requires recharts package<br/>
                  Run: <code>npm install recharts</code>
                </Typography>
                {/* Simple bar representation */}
                <Box sx={{ mt: 2 }}>
                  {chartData.slice(0, 5).map((point, index) => (
                    <Box key={index} sx={{ mb: 2 }}>
                      <Typography variant="body2">{point.date}</Typography>
                      <Box sx={{
                        width: `${Math.min((point.value / (kpi.target_value || point.value)) * 100, 100)}%`,
                        height: 20,
                        backgroundColor: '#8884d8',
                        borderRadius: 1
                      }} />
                      <Typography variant="caption">{point.value}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Pie Chart */}
            {kpi.visualization_type === 'pie_chart' && chartData.length > 0 && (
              <Box sx={{ mt: 2, p: 2, border: '1px dashed #ccc', borderRadius: 2 }}>
                <Typography variant="body2" color="textSecondary" align="center" sx={{ py: 4 }}>
                  🥧 Pie chart visualization requires recharts package<br/>
                  Run: <code>npm install recharts</code>
                </Typography>
                {/* Simple pie data representation */}
                <Box sx={{ mt: 2 }}>
                  {chartData.slice(0, 5).map((point, index) => (
                    <Box key={index} display="flex" justifyContent="space-between" sx={{ mb: 1 }}>
                      <Typography variant="body2">{point.date}</Typography>
                      <Typography variant="body2">{point.value}</Typography>
                      <Box sx={{
                        width: 16,
                        height: 16,
                        backgroundColor: COLORS[index % COLORS.length],
                        borderRadius: '50%'
                      }} />
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Gauge/Percentage */}
            {kpi.visualization_type === 'percentage' && latestValue && kpi.target_value && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="h4" align="center" sx={{ mb: 2 }}>
                  {((parseFloat(latestValue.value) / parseFloat(kpi.target_value)) * 100).toFixed(1)}%
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={(parseFloat(latestValue.value) / parseFloat(kpi.target_value)) * 100}
                  sx={{ height: 20, borderRadius: 10 }}
                />
                <Typography variant="body2" color="textSecondary" align="center" sx={{ mt: 1 }}>
                  {parseFloat(latestValue.value).toLocaleString()} / {parseFloat(kpi.target_value).toLocaleString()} {kpi.unit}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>
    );
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <SimpleCard title="Loading Dashboard...">
          <LinearProgress />
        </SimpleCard>
      </Box>
    );
  }

  if (!dashboard) {
    return (
      <Box sx={{ p: 3 }}>
        <SimpleCard title="Dashboard Not Found">
          <Typography>Dashboard not found or you don't have access.</Typography>
        </SimpleCard>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <SimpleCard title={dashboard.name}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="body2" color="textSecondary">
            {dashboard.description}
          </Typography>
          <Box display="flex" gap={1}>
            <Tooltip title="Refresh">
              <IconButton onClick={loadDashboard}>
                <Refresh />
              </IconButton>
            </Tooltip>
            <Button variant="contained" startIcon={<Add />} onClick={handleAddWidget}>
              Add Widget
            </Button>
          </Box>
        </Box>

        <Grid container spacing={2}>
          {dashboard.Widgets && dashboard.Widgets.length > 0 ? (
            dashboard.Widgets
              .filter(w => w.is_visible)
              .sort((a, b) => a.sort_order - b.sort_order)
              .map(widget => renderWidget(widget))
          ) : (
            <Grid  size={{xs:12}}>
              <Card>
                <CardContent>
                  <Typography align="center" color="textSecondary" sx={{ py: 4 }}>
                    No widgets added yet. Click "Add Widget" to get started.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </SimpleCard>

      {/* Add Widget Modal */}
      <AddWidgetModal
        open={widgetModal.open}
        dashboardId={dashboardId}
        kpis={kpis}
        onClose={() => setWidgetModal({ open: false, widget: null })}
        onSave={loadDashboard}
      />
    </Box>
  );
}

// Add Widget Modal
function AddWidgetModal({ open, dashboardId, kpis, onClose, onSave }) {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    kpi_definition_id: '',
    title: '',
    position_x: 0,
    position_y: 0,
    width: 6,
    height: 4,
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!formData.kpi_definition_id) {
      dispatch(showSnackBar({ message: 'Please select a KPI', severity: 'error' }));
      return;
    }

    try {
      setLoading(true);
      const selectedKpi = kpis.find(k => k.id === formData.kpi_definition_id);
      await APIKit.post('/dashboard-widgets', {
        ...formData,
        dashboard_id: dashboardId,
        title: formData.title || selectedKpi.name,
      });
      dispatch(showSnackBar({ message: 'Widget added successfully', severity: 'success' }));
      onSave();
      onClose();
      setFormData({
        kpi_definition_id: '',
        title: '',
        position_x: 0,
        position_y: 0,
        width: 6,
        height: 4,
      });
    } catch (error) {
      dispatch(showSnackBar({ message: 'Error adding widget', severity: 'error' }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Widget</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControl fullWidth>
            <InputLabel>Select KPI</InputLabel>
            <Select
              value={formData.kpi_definition_id}
              onChange={(e) => {
                const selectedKpi = kpis.find(k => k.id === e.target.value);
                setFormData({
                  ...formData,
                  kpi_definition_id: e.target.value,
                  title: selectedKpi?.name || '',
                });
              }}
            >
              {kpis.map(kpi => (
                <MenuItem key={kpi.id} value={kpi.id}>
                  {kpi.name} ({kpi.category})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Widget Title"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            fullWidth
          />

          <Grid container spacing={2}>
            <Grid  size={{md:6}}>
              <TextField
                label="Width (columns)"
                type="number"
                value={formData.width}
                onChange={(e) => setFormData({ ...formData, width: parseInt(e.target.value) })}
                fullWidth
                inputProps={{ min: 1, max: 12 }}
              />
            </Grid>
            <Grid size={{md:6}}>
              <TextField
                label="Height (rows)"
                type="number"
                value={formData.height}
                onChange={(e) => setFormData({ ...formData, height: parseInt(e.target.value) })}
                fullWidth
                inputProps={{ min: 1 }}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          Add Widget
        </Button>
      </DialogActions>
    </Dialog>
  );
}
